// data.js — FULL FILE (session cookies + CORS + /api paths)
import moment from "moment-timezone";
import { isNight } from "./utils/sunTimes";
import { getFirstReg, getAge } from "./utils/vehicle";
import moon from "./Assets/moon.svg";
import logos from "./Assets/logos.json";

export const API = "https://api.watchgrid.co.uk/api";

//export const API = "https://100.74.30.36/api";
// export const API = "https://raspberrypi4.local/api";
export const AUTH_BASE = API.replace(/\/api\/?$/i, "");

/* ---------------- time helpers ---------------- */
const formatTimeBits = (iso) => {
  const tz = "Europe/London";
  const m = moment.utc(iso).tz(tz);
  if (!m.isValid()) return { time: "", dateLabel: "", full: "" };

  const now = moment.tz(tz);
  const today = now.clone().startOf("day");
  const day = m.clone().startOf("day");
  const diff = day.diff(today, "days");

  let dateLabel;
  if (diff === 0) dateLabel = "Today";
  else if (diff === -1) dateLabel = "Yesterday";
  else {
    const monthsAgo = now.clone().startOf("month").diff(m.clone().startOf("month"), "months");
    const fmt = monthsAgo > 11 ? "ddd D MMM [']YY" : "ddd D MMM";
    dateLabel = m.format(fmt);
  }

  return { time: m.format("HH:mm:ss"), dateLabel, full: m.format("YYYY-MM-DD HH:mm:ss") };
};

/* ---------------- assets/helpers ---------------- */
export const getLogoForMake = (make) => {
  if (!make) return null;
  const upper = String(make).toUpperCase();
  const hit = logos.find((l) => l?.name && l.name.toUpperCase() === upper);
  return hit?.image?.optimized || null;
};

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

/* ---------------- colour helpers ---------------- */
const VEHICLE_COLOUR_MAP = {
  black: "#1b1b1b",
  white: "#f5f5f5",
  silver: "#c0c4c7",
  grey: "#8a8f98",
  gray: "#8a8f98",
  blue: "#16599bff",
  red: "#d32f2f",
  green: "#2e7d32",
  yellow: "#f6c945",
  orange: "#fb8c00",
  brown: "#8d6e63",
  beige: "#d7c4a3",
  gold: "#d4af37",
  purple: "#8e44ad",
  pink: "#e91e63",
  maroon: "#800000",
};

export const prettyColourHex = (raw) => {
  if (!raw) return "#cccccc";
  const key = String(raw).trim().toLowerCase();
  if (VEHICLE_COLOUR_MAP[key]) return VEHICLE_COLOUR_MAP[key];
  const norm = key.replace(/[\s-]/g, "");
  if (norm.includes("grey")) return VEHICLE_COLOUR_MAP.grey;
  if (norm.includes("gray")) return VEHICLE_COLOUR_MAP.gray;
  try {
    const test = document.createElement("div");
    test.style.color = key;
    if (test.style.color) return key;
  } catch { }
  return "#cccccc";
};

/* ---------------- columns for MainTable ---------------- */
export const columns = [
  {
    Header: "VRM",
    accessor: "vrm",
    Cell: ({ value }) => <span className="vrm-cell">{value}</span>,
  },
  {
    Header: "Time",
    accessor: "hitTime",
    width: 130,
    minWidth: 120,
    maxWidth: 150,
    Cell: ({ cell: { row } }) => {
      const ts = row.original.hitTime;
      const { time, full } = formatTimeBits(ts);
      const night = isNight(ts, "Europe/London", 51.5074, -0.1278);
      const hhmm = time.slice(0, 5);
      return (
        <div className="timedate-cell">
          <div className="chip-wrap chip-wrap--stack" title={full} aria-label={full}>
            <span className="chip">
              <time className="chip-time" dateTime={full}>
                {hhmm}
              </time>
              {night && <img src={moon} alt="Nighttime" className="icon" />}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    Header: "Vehicle",
    accessor: "make",
    minWidth: 600,
    width: 600,
    maxWidth: 600,
    Cell: ({ cell: { row } }) => {
      const make = row.original.make || "";
      const colour = row.original.colour || "";
      const logoUrl = getLogoForMake(make);
      const colourLabel = capFirst(colour);
      const bandHex = prettyColourHex(colour);

      const firstRegRaw = row.original.monthOfFirstRegistration;
      const monthYear = getFirstReg(firstRegRaw);
      const ageLong = getAge(firstRegRaw);

      const titleBits = [make, colourLabel];
      if (ageLong) titleBits.push(ageLong);
      if (monthYear) titleBits.push(monthYear);
      const title = titleBits.join(" • ");

      return (
        <div className="vehicle-cell" title={title} aria-label={title}>
          <div className="vehicle-logo-wrapper">
            {logoUrl && <img className="vehicle-logo" src={logoUrl} alt={`${make} logo`} />}
          </div>

          <div className="vehicle-lines">
            <div className="vehicle-topline">
              <span className="vehicle-make">{make || "—"}</span>
            </div>

            <div className="vehicle-subline">
              {ageLong && <div className="vehicle-age">{ageLong}</div>}
              {monthYear && <div className="vehicle-firstreg">{monthYear}</div>}
            </div>
          </div>
          <div className="vehicle-right">
            <div
              className="vehicle-colourcircle"
              style={{ backgroundColor: bandHex || "#ccc" }}
              aria-label={colourLabel || "colour"}
              title={colourLabel || "colour"}
            />
            {colourLabel && <div className="vehicle-colourlabel">{colourLabel}</div>}
          </div>
        </div>
      );
    },
  },
  {
    Header: "Tags",
    accessor: "tags",
    minWidth: 260,
    width: 350,
    maxWidth: 400,
    Cell: ({ cell: { row } }) => {
      const tags = Array.isArray(row.original.tags) ? row.original.tags : [];

      const weight = (s) => (s === "severe" ? 0 : s === "warning" ? 1 : 2);
      const sorted = [...tags].sort((a, b) => weight(a.status) - weight(b.status));

      const clsFor = (s) => (s === "severe" ? "bad" : s === "warning" ? "unk" : "ok");

      if (sorted.length === 0) {
        return (
          <div className="tags-cell">
            <div className="tags-row">
              <div className="status-pill ok" title="No tags">
                —
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="tags-cell">
          <div className="tags-row" role="list">
            {sorted.map((t) => {
              const titleBits = [t.label];
              if (t.reason) titleBits.push(t.reason);
              if (t.expires_at) titleBits.push(`Expires: ${t.expires_at}`);
              const title = titleBits.join(" • ");

              return (
                <div
                  key={`${t.key}-${t.status}-${t.expires_at ?? ""}`}
                  className={`status-pill ${clsFor(t.status)} status-pill--tag`}
                  title={title}
                  aria-label={title}
                  role="listitem"
                >
                  {t.label}
                </div>
              );
            })}
          </div>
        </div>
      );
    },
  },
  {
    Header: "Thumbnail",
    accessor: "hitId",
    minWidth: 210,
    maxWidth: 210,
    Cell: ({ cell: { row } }) => {
      const o = row.original;

      // Use presigned S3 URL directly if available
      if (o.plate_crop_url) {
        return <img className="numberplate" src={o.plate_crop_url} alt="plate preview" />;
      }

      // Fall back to old getThumbnail endpoint for legacy hits
      if (o.hasNumberPlateImage > 0 && o.hitId) {
        const src = `${API}/getThumbnail?hit_id=${o.hitId}`;
        return <img className="numberplate" src={src} alt="plate preview" />;
      }

      const dir = (o.direction || "").toLowerCase();
      const isForward = dir.includes("forward") || dir.includes("front");

      return (
        <p className="reg-wrapper">
          <span className={isForward ? "registration-ui-front" : "registration-ui-rear"}>{o.vrm}</span>
        </p>
      );
    },
  },
];

/* ---------------- fetch helper (always send cookies) ---------------- */
async function fetchJson(url, opts = {}) {
  const init = {
    credentials: "include",
    mode: "cors",
    ...opts,
  };

  const baseHeaders = { Accept: "application/json" };
  if (init.body && !(init.body instanceof FormData)) {
    init.headers = { "Content-Type": "application/json", ...(init.headers || {}), ...baseHeaders };
    if (typeof init.body !== "string") init.body = JSON.stringify(init.body);
  } else {
    init.headers = { ...(init.headers || {}), ...baseHeaders };
  }

  // NEW: if a forced reset is already active, don't even call the server
  if (typeof window !== "undefined" && window.__forcePwChange) {
    try { window.dispatchEvent(new CustomEvent("password-change-required", { detail: { at: Date.now() } })); } catch { }
    return { success: false, error: "PASSWORD_CHANGE_REQUIRED", data: [], rows: [] };
  }

  const res = await fetch(url, init);
  const json = await res.json().catch(() => ({}));

  // NEW: handle 428 / password-change signal without throwing
  const pwReset = res.status === 428
    || String(json?.error || "").toUpperCase() === "PASSWORD_CHANGE_REQUIRED"
    || json?.must_change_password === true;

  if (pwReset) {
    try {
      window.__forcePwChange = true;
      window.dispatchEvent(new CustomEvent("password-change-required", { detail: { at: Date.now() } }));
    } catch { }
    // Return a safe shape; callers can keep rendering with empty data.
    return { success: false, error: "PASSWORD_CHANGE_REQUIRED", data: [], rows: [] };
  }

  if (!res.ok) {
    const err = new Error(json?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = json;
    if (res.status === 401) {
      try { window.dispatchEvent(new CustomEvent("unauthenticated", { detail: { at: Date.now() } })); } catch { }
    }
    throw err;
  }

  return json;
}

/* ---------------- hits fetch ---------------- */
export const getData = async (pageNo = 1, rowsPerPage = 25, vrmFilter, startDateTime, endDateTime, filters) => {
  try {
    const params = new URLSearchParams({
      page: String(pageNo),
      size: String(rowsPerPage),
      vrm: vrmFilter ?? "",
    });

    if (startDateTime) params.set("start", moment(startDateTime).toISOString());
    if (endDateTime) params.set("end", moment(endDateTime).toISOString());

    if (filters) {
      if (filters.severity === "severe") params.set("severity", "severe");
      else if (filters.severity === "warnOrSevere") params.set("severity", "warn_severe");

      if (filters.dayNight && filters.dayNight !== "any") params.set("dayNight", filters.dayNight);

      if (Array.isArray(filters.tagSources) && filters.tagSources.length && filters.tagSources.length < 2) {
        params.set("tag_sources", filters.tagSources.join(","));
      }

      if (Array.isArray(filters.tagKeys) && filters.tagKeys.length) {
        params.set("tags", filters.tagKeys.join(","));
      }

      if (Array.isArray(filters.makes) && filters.makes.length) {
        params.set("makes", filters.makes.join(","));
      }

      if (filters.alertOnly) params.set("alert_only", "true");
    }

    const url = `${API}/getHits?${params.toString()}`;
    return await fetchJson(url);
  } catch {
    return { pages: 1, count: 0, data: [] };
  }
};

/* ---------------- broadcast helpers ---------------- */
function announceAlertListUpdated(reason = "changed") {
  try {
    window.dispatchEvent(new CustomEvent("alertlist-updated", { detail: { reason, at: Date.now() } }));
  } catch { }
}

/* ---------------- misc APIs used elsewhere ---------------- */
export const getHitCount = async (startDate, endDate, vrm) => {
  try {
    const url = `${API}/getHitCount?startDate=${startDate}&endDate=${endDate}&vrm=${vrm}`;
    const json = await fetchJson(url);
    return json;
  } catch {
    return { data: [] };
  }
};

export async function listUserTags() {
  const url = `${API}/tags?scope=user`;
  const { data } = await fetchJson(url);
  return (data || []).map((t) => ({
    key: t.key,
    label: t.label || t.key,
    default_status: t.default_status || "normal",
  }));
}

export async function createUserTag({ key, label, description, default_status = "normal" }) {
  const url = `${API}/tags`;
  const json = await fetchJson(url, {
    method: "POST",
    body: { key, label, description, default_status },
  });
  return json;
}

export async function updateUserTag(key, { label, description, default_status }) {
  const url = `${API}/tags/${encodeURIComponent(key)}`;
  const body = {};
  if (label !== undefined) body.label = label;
  if (description !== undefined) body.description = description;
  if (default_status !== undefined) body.default_status = default_status;
  const json = await fetchJson(url, { method: "PUT", body });
  return json;
}

export async function deleteUserTag(key) {
  const url = `${API}/tags/${encodeURIComponent(key)}`;
  const json = await fetchJson(url, { method: "DELETE" });
  return json;
}

export async function addUserTagToVehicle(vrm, { tagKey, status, reason, expiresAt }) {
  const url = `${API}/vehicles/${encodeURIComponent(vrm)}/tags`;
  const json = await fetchJson(url, {
    method: "POST",
    body: { tagKey, status, reason, expiresAt },
  });
  return json;
}

export function normalizeKey(s = "") {
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export async function fetchVehicleTags(vrm) {
  const url = `${API}/vehicles/${encodeURIComponent(vrm)}/tags`;
  const { data } = await fetchJson(url);
  return data || [];
}

export async function removeUserTagFromVehicle(vrm, tagKey) {
  const url = `${API}/vehicles/${encodeURIComponent(vrm)}/tags/${encodeURIComponent(tagKey)}`;
  const json = await fetchJson(url, { method: "DELETE" });
  return json;
}

export async function fetchMotSummary(vrm) {
  const url = `${API}/vehicles/${encodeURIComponent(vrm)}/mot`;
  const { data } = await fetchJson(url);
  return data || null;
}

export async function fetchMotTests(vrm, limit = 10, offset = 0) {
  const url = `${API}/vehicles/${encodeURIComponent(vrm)}/mot/tests?limit=${limit}&offset=${offset}`;
  const { data } = await fetchJson(url);
  return Array.isArray(data) ? data : [];
}

/* ---------------- dashboard helpers ---------------- */
export async function listSystemTags() {
  try {
    const url = `${API}/tags?scope=system`;
    const { data } = await fetchJson(url);
    return (data || []).map((t) => ({
      key: t.key,
      label: t.label || t.key,
      default_status: t.default_status || "normal",
    }));
  } catch {
    return [];
  }
}

export async function listMakes() {
  try {
    const url = `${API}/listManufacturers`;
    const { data } = await fetchJson(url);
    return Array.isArray(data) ? [...new Set(data)].sort((a, b) => String(a).localeCompare(String(b))) : [];
  } catch {
    return [];
  }
}

export async function fetchThroughput(period = "today") {
  try {
    const url = `${API}/statsThroughput?period=${encodeURIComponent(period)}`;
    const json = await fetchJson(url);
    return json;
  } catch {
    return { success: false, period, unit: "day", total: 0, series: [] };
  }
}

export async function fetchRecentAlerts(limit = 3) {
  try {
    const url = `${API}/alertsRecent?limit=${encodeURIComponent(limit)}`;
    return await fetchJson(url);
  } catch {
    return { success: false, rows: [] };
  }
}

/* ---------------- alert list core ---------------- */
export async function listAlertVehicles(q = "", page = 1, size = 10) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("page", String(page));
  params.set("size", String(size));

  const url = `${API}/alert/list?${params.toString()}`;
  const data = await fetchJson(url, { method: "GET" });

  if (data?.success !== true) {
    throw new Error(data?.error || "Failed to load alert list");
  }
  return data; // { success, total, rows, page, pages }
}

// ---- Alert Types cache + helpers (drives "no types" UX) ----
let _alertTypes = [];
let _alertTypesLoadedAt = 0;

export async function listAlertTypes() {
  const url = `${API}/alertTypes`;
  const { data } = await fetchJson(url);
  return Array.isArray(data) ? data : [];
}

export function getAlertTypesSnapshot() {
  return _alertTypes.slice();
}

export function alertTypeCount() {
  return _alertTypes.length;
}

export function hasAlertTypes() {
  return _alertTypes.length > 0;
}

export async function refreshAlertTypes({ force = false } = {}) {
  const now = Date.now();
  if (!force && now - _alertTypesLoadedAt < 4000 && _alertTypes.length) return _alertTypes;
  try {
    _alertTypes = await listAlertTypes();
    _alertTypesLoadedAt = now;
    try {
      window.dispatchEvent(
        new CustomEvent("alerttypes-updated", { detail: { at: now, count: _alertTypes.length } })
      );
    } catch { }
  } catch {
    _alertTypes = [];
    _alertTypesLoadedAt = now;
  }
  return _alertTypes;
}

/* Add/Remove/Check alerts (type-aware add) */
export async function addAlertVehicle(vrm, meta = {}) {
  // If 0 types: send as-is (backend will treat as no type)
  // If 1 type: auto-assign that type unless caller provided one
  if (hasAlertTypes() && alertTypeCount() === 1 && !meta.typeKey) {
    const only = _alertTypes[0];
    meta = { ...meta, typeKey: only.key };
  }

  const url = `${API}/alert/add`;
  const json = await fetchJson(url, { method: "POST", body: { vrm, ...meta } });
  if (json?.success) announceAlertListUpdated("added");
  return json;
}

export async function removeAlertVehicle(vrm) {
  const url = `${API}/alert/remove/${encodeURIComponent(vrm)}`;
  const json = await fetchJson(url, { method: "DELETE" });
  if (json?.success) announceAlertListUpdated("removed");
  return json;
}

export async function checkAlertVehicle(vrm) {
  const url = `${API}/alert/check/${encodeURIComponent(vrm)}`;
  const json = await fetchJson(url);
  return !!json?.on_list;
}

// Update alert meta/type for a VRM
export async function updateAlertVehicle(vrm, { note, typeKey } = {}) {
  const url = `${API}/alertUpdate/${encodeURIComponent(vrm)}`;
  const json = await fetchJson(url, {
    method: "PUT", // matches routes.php
    body: {
      ...(note !== undefined ? { note } : {}),
      ...(typeKey !== undefined ? { typeKey } : {}),
    },
  });

  if (json?.error === "PASSWORD_CHANGE_REQUIRED") return [];


  if (json?.success !== true) {
    throw new Error(json?.error || "Update failed");
  }
  try {
    window.dispatchEvent(new CustomEvent("alertlist-updated", { detail: { reason: "edited", at: Date.now() } }));
  } catch { }
  return json;
}

// ---- Alert Type CRUD (UI uses these) ----
export async function createAlertType({ key, label, color_hex }) {
  const url = `${API}/alertTypes`;
  const json = await fetchJson(url, {
    method: "POST",
    body: { key, label, color_hex },
  });

  if (json?.error === "PASSWORD_CHANGE_REQUIRED") return [];


  if (json?.success !== true) {
    throw new Error(json?.error || "Create failed");
  }
  return json;
}

export async function updateAlertType(key, { label, color_hex }) {
  const url = `${API}/alertTypes/${encodeURIComponent(key)}`;
  const json = await fetchJson(url, {
    method: "PUT",
    body: { label, color_hex },
  });

  if (json?.error === "PASSWORD_CHANGE_REQUIRED") return [];

  if (json?.success !== true) {
    throw new Error(json?.error || "Update failed");
  }
  return json;
}

export async function deleteAlertType(key) {
  const url = `${API}/alertTypes/${encodeURIComponent(key)}`;
  const json = await fetchJson(url, { method: "DELETE" });

  if (json?.error === "PASSWORD_CHANGE_REQUIRED") return [];

  if (json?.success !== true) {
    throw new Error(json?.error || "Delete failed");
  }
  return json;
}

/* ---------------- Top tags & makes/colours (used by Dashboard) ---------------- */
export async function fetchTopTags(period = "24h", severity = "normal") {
  try {
    const url = `${API}/statsTopTags?period=${encodeURIComponent(period)}&severity=${encodeURIComponent(severity)}`;
    return await fetchJson(url);
  } catch {
    return { success: false, period, severity, rows: [] };
  }
}

export async function fetchTopMakesColours(period = "24h", limit = 3) {
  try {
    const url = `${API}/statsTopMakesColours?period=${encodeURIComponent(period)}&limit=${encodeURIComponent(limit)}`;
    const json = await fetchJson(url);
    const makes = Array.isArray(json?.top_makes) ? json.top_makes : [];
    const colours = Array.isArray(json?.top_colours) ? json.top_colours : [];
    return { success: !!json?.success, period: json?.period || period, makes, colours };
  } catch {
    return { success: false, period, makes: [], colours: [] };
  }
}

export async function fetchHitById(id) {
  if (!id) throw new Error("Missing hit ID");
  const url = `${API}/getHit/${encodeURIComponent(id)}`;
  const json = await fetchJson(url, {
    headers: { Accept: "application/json" },
  });
  // controller returns { success: true, data: row }
  return json?.data ?? json;
}

export async function fetchTopVisitors(period = "24h", limit = 3) {
  const url = `${API}/statsTopVisitors?period=${encodeURIComponent(period)}&limit=${limit}`;
  const json = await fetchJson(url);

  if (json?.error === "PASSWORD_CHANGE_REQUIRED") return [];

  if (json?.success === false) {
    throw new Error(json?.error || "Failed to load top visitors");
  }
  return json.rows || json.data || [];
}

/* ---------------- cameras (CRUD + licence key) ---------------- */

export async function listCameras() {
  const { data } = await fetchJson(`${API}/cameras`, { method: "GET" });
  return Array.isArray(data) ? data : [];
}

export async function createCamera(payload) {
  const json = await fetchJson(`${API}/cameras`, {
    method: "POST",
    body: payload,
  });
  return json; // { success:true, data:{ id, licence_key } }
}

export async function updateCamera(id, payload) {
  const json = await fetchJson(`${API}/cameras/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: payload,
  });
  return json;
}

export async function deleteCamera(id) {
  const json = await fetchJson(`${API}/cameras/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  return json;
}

export async function regenerateLicenceKey(id) {
  const json = await fetchJson(
    `${API}/cameras/${encodeURIComponent(id)}/licence/regenerate`,
    { method: "POST" }
  );
  // Returns { success:true, licence_key: "XXXX-XXXX-XXXX-XXXX" }
  return json?.licence_key ?? null;
}


/* ---------------- users (CRUD + password reset) ---------------- */

export async function listUsers() {
  const { data } = await fetchJson(`${API}/users`, { method: "GET" });
  return Array.isArray(data) ? data : [];
}

export async function createUser({ email, is_admin }) {
  const json = await fetchJson(`${API}/users`, {
    method: "POST",
    body: { email, is_admin: !!is_admin },
  });
  return json; // { success, id, temp_password }
}

export async function updateUser(id, { email, is_admin, is_active }) {
  const json = await fetchJson(`${API}/users/${encodeURIComponent(id)}`, {
    method: "POST", // matches backend route
    body: {
      ...(email !== undefined ? { email } : {}),
      ...(is_admin !== undefined ? { is_admin: !!is_admin } : {}),
      ...(is_active !== undefined ? { is_active: !!is_active } : {}),
    },
  });
  return json;
}

export async function deleteUser(id) {
  const json = await fetchJson(`${API}/users/${encodeURIComponent(id)}/delete`, {
    method: "POST",
  });
  return json;
}

export async function resetUserPassword(id) {
  const json = await fetchJson(`${API}/users/${encodeURIComponent(id)}/reset-password`, {
    method: "POST",
  });
  // Normalize shape just in case the controller nests it later
  return {
    success: json?.success !== false,
    temp_password: json?.temp_password ?? json?.data?.temp_password ?? null,
  };
}